import React, { useEffect, useState } from 'react';
import NumberInput from '../../components/NumberInput';
import { Tooltip } from '@mui/material';
import useBrickAnalogDelivery from './useBrickAnalogDelivery';

// Добавляем стили для анимации цен
const priceAnimationStyles = `
  @keyframes priceChange {
    0% { 
      transform: scale(1); 
      color: inherit; 
    }
    50% { 
      transform: scale(1.1); 
      color: #1976d2; 
    }
    100% { 
      transform: scale(1); 
      color: inherit; 
    }
  }
  
  .price-animation {
    animation: priceChange 0.5s ease-in-out;
    display: inline-block;
    font-weight: bold;
  }
  
  .price-highlight {
    background-color: rgba(25, 118, 210, 0.1);
    border-radius: 4px;
    padding: 2px 6px;
    transition: background-color 0.3s ease;
  }
`;

// Добавляем стили в документ при первом рендере
if (typeof document !== 'undefined' && !document.getElementById('price-animation-styles')) {
  const styleElement = document.createElement('style');
  styleElement.id = 'price-animation-styles';
  styleElement.textContent = priceAnimationStyles;
  document.head.appendChild(styleElement);
}

const AnalogsList = ({
  items, onSelectAnalog, colSpan, setExpandedRows, currentProduct, options, paymentMethod, forceUpdate,
  addressAndCoordinates, selectedRegion, selfPickUp, deliveryMethod,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [quantities, setQuantities] = useState({});
  const [errors, setErrors] = useState({});
  const [prevPaymentMethod, setPrevPaymentMethod] = useState(paymentMethod);
  const [animatingPrices, setAnimatingPrices] = useState(new Set());
  const [localItems, setLocalItems] = useState(items);
  const { requestDelivery, getDeliveryState } = useBrickAnalogDelivery({
    addressAndCoordinates, selectedRegion, selfPickUp, deliveryMethod, paymentMethod,
  });

  const handleQuantityChange = (index, value) => {
    if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
      setQuantities((prev) => ({
        ...prev,
        [index]: value,
      }));
      setErrors((prev) => ({
        ...prev,
        [index]: parseFloat(value) === 0 || value === "" ? 'Количество не может быть 0' : undefined,
      }));
    }
  };

  const handleSelect = (item, index) => {
    const quantity = quantities[index] === '' ? 0 : parseFloat(quantities[index]) || 0;
    if (quantity === 0) {
      return;
    }

    // Вызываем функцию выбора аналога с новым количеством
    onSelectAnalog(item.value, currentProduct.id, quantity);
    setExpandedRows({});
  };

  // Создаем объект для текущего выбранного товара в формате аналога
  const currentProductAsAnalog = React.useMemo(() => {
    if (!currentProduct || !options) return null;

    // Получаем исходные данные из options по имени товара
    const selectedData = options[currentProduct.name] || {};
    console.log('🔍 currentProduct.quantity:', currentProduct.quantity);
    console.log('🔍 AnalogsList currentProductAsAnalog recalculated with options keys:', Object.keys(options).slice(0, 3));
    console.log('🔍 selectedData.MANUFACTURER:', selectedData.MANUFACTURER);
    console.log('🔍 paymentMethod:', paymentMethod);

    // Определяем цену в зависимости от способа оплаты
    const currentPrice = paymentMethod === "cash" ? selectedData.PRICE_CASH : selectedData.PRICE_NONCASH;
    const currentPriceValue = currentPrice ? parseFloat(currentPrice) || 0 : 0;
    const isPriceOnRequest = !currentPriceValue || currentPriceValue <= 0;

    return {
      label: currentProduct.name || 'Текущий товар',
      value: currentProduct.name,
      details: {
        manufacturer: selectedData.MANUFACTURER || 'Не указано',
        category: currentProduct.product_group || selectedData.CATEGORY || 'Не указано',
        density: selectedData.DENSITY ? `${selectedData.DENSITY}` : null,
        compression: selectedData.COMPRESSION,
        price: isPriceOnRequest ? null : currentPrice,
        sheetsInsulation: currentProduct.unit || 'шт',
        brickFormat: selectedData.FORMAT || null,
        brickVoidness: selectedData.DENSITY_REPEAT || null,
        brickStrength: selectedData.STRENGTH || null,
      },
      // Текущие цены товара (абсолютные значения)
      priceDifferencePerPackage: currentPriceValue,
      priceDifferencePerSquareMeter: currentPriceValue && (selectedData.AREA_SQUARE || selectedData.AREA_OSB)
        ? Math.round(currentPriceValue / parseFloat(String(selectedData.AREA_SQUARE || selectedData.AREA_OSB).replace(",", ".")))
        : 0,
      // Для OSB цена уже указана за лист, для остальных делим на количество листов
      priceDifferencePerSheet: selectedData.CATEGORY?.includes("OSB")
        ? Math.round(currentPriceValue)
        : currentPriceValue && selectedData.SHEETS_INSULATION
          ? Math.round(currentPriceValue / parseFloat(selectedData.SHEETS_INSULATION))
          : 0,
      volumeBenefit: 0, // Нет выгоды для текущего товара
      requiredSheets: (currentProduct.quantity == null || currentProduct.quantity === '' || currentProduct.quantity === 0 || isNaN(currentProduct.quantity))
        ? '100'
        : currentProduct.quantity,
      isCurrentProduct: true, // Флаг для выделения
      isPriceOnRequest, // Флаг для "по запросу"
    };
  }, [currentProduct, options, paymentMethod, forceUpdate]);

  // items уже содержат правильные цены из useAnalogs, дополнительно обновлять не нужно
  // items уже содержат правильные цены из useAnalogs, дополнительно обновлять не нужно
  const itemsWithUpdatedPrices = localItems;

  console.log('🔍 itemsWithUpdatedPrices result:', itemsWithUpdatedPrices.map(item => ({
    label: item.label,
    isCurrentProduct: item.isCurrentProduct,
    price: item.details.price
  })));

  // Синхронизируем localItems с props.items при изменении items
  useEffect(() => {
    setLocalItems(items);
    // Отладочная информация
    console.log('🔍 AnalogsList - items updated:', items.map(item => ({
      label: item.label,
      isCurrentProduct: item.isCurrentProduct,
      detailsPrice: item.details.price,
      paymentMethod
    })));
  }, [items]);

  // Отладочная информация при рендере
  console.log('🔍 AnalogsList rendering:', {
    hasItems: items.length > 0,
    itemCount: items.length,
    paymentMethod
  });

  // Кирпич продаётся партиями в тысячи штук при малой цене за штуку — округление до целого
  // рубля (как для остальных аналогов) даёт заметную переплату, поэтому для него храним и
  // показываем цену и итог с копейками (2 знака), без округления до целого.
  const isBrickCategoryValue = (category) => String(category || '').trim().toLowerCase().startsWith('кирпич');
  const isBrickContext = isBrickCategoryValue(currentProductAsAnalog?.details?.category)
    || items.some((item) => item.isBrickAnalog);

  // Доставка для кирпича считается изолированно на бэкенде (см. brickDeliveryPricing.js) —
  // запрашиваем её только для реально показанных строк (текущий товар + аналоги с ценой),
  // а не для всей номенклатуры.
  useEffect(() => {
    if (!isBrickContext) return;

    if (currentProductAsAnalog?.value && !currentProductAsAnalog.isPriceOnRequest) {
      requestDelivery(
        currentProductAsAnalog.value,
        options[currentProductAsAnalog.value],
        parseFloat(currentProductAsAnalog.requiredSheets) || 0
      );
    }

    itemsWithUpdatedPrices.forEach((item) => {
      if (!item.isBrickAnalog) return;
      requestDelivery(item.value, options[item.value], parseFloat(item.requiredSheets) || 0);
    });
  }, [isBrickContext, currentProductAsAnalog, itemsWithUpdatedPrices, options, requestDelivery]);

  const getBrickDeliveryState = (item) => {
    const quantity = parseFloat(item.requiredSheets) || 0;
    return item.value ? getDeliveryState(item.value, quantity) : null;
  };

  const getItemPrice = React.useCallback((item) => {
    const basePrice = item.details.price;
    const isBrickRow = item.isBrickAnalog || (item.isCurrentProduct && isBrickContext);
    if (!isBrickRow) {
      // Используем обновленную цену напрямую из объекта
      return basePrice;
    }

    const deliveryState = getBrickDeliveryState(item);
    if (deliveryState?.status !== 'ok') {
      return basePrice;
    }
    return (parseFloat(basePrice) || 0) + deliveryState.perUnit;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBrickContext, getDeliveryState]);

  // Цена кирпича без доставки ничего не говорит о том, что реально дешевле —
  // производство подешевле по прайсу может стоять в разы дальше и быть дороже в
  // сумме. Поэтому для кирпича filter+sort делаем здесь, по цене С доставкой, а не
  // в useAnalogs.js (там цена ещё без доставки). Пока доставка не посчитана хотя бы
  // по одному кандидату — не показываем список вовсе, чтобы не показать неверный
  // порядок/состав, который потом "переедет".
  const brickAnalogItems = isBrickContext ? itemsWithUpdatedPrices.filter((item) => item.isBrickAnalog) : [];
  const isCurrentBrickOnRequest = !!currentProductAsAnalog?.isPriceOnRequest;
  const brickDeliveryPending = isBrickContext && (
    (currentProductAsAnalog && !isCurrentBrickOnRequest && !getBrickDeliveryState(currentProductAsAnalog)) ||
    brickAnalogItems.some((item) => {
      const state = getBrickDeliveryState(item);
      return !state || state.status === 'loading';
    })
  );
  const currentBrickEffectivePrice = currentProductAsAnalog ? parseFloat(getItemPrice(currentProductAsAnalog)) || 0 : 0;
  const sortedBrickAnalogItems = isBrickContext
    ? [...brickAnalogItems]
      .filter((item) => isCurrentBrickOnRequest || (parseFloat(getItemPrice(item)) || 0) < currentBrickEffectivePrice)
      .sort((a, b) => (parseFloat(getItemPrice(a)) || 0) - (parseFloat(getItemPrice(b)) || 0))
    : [];

  const roundToPrecision = (value, precision) => {
    const factor = 10 ** precision;
    return Math.round((Number(value) || 0) * factor) / factor;
  };

  // Компонент для отображения анимированной цены
  const AnimatedPrice = ({ price, index, isAnimating, precision = 0 }) => {
    const rounded = roundToPrecision(price, precision);
    return (
      <span className={isAnimating ? 'price-animation price-highlight' : ''}>
        {rounded.toLocaleString(undefined, precision > 0 ? { minimumFractionDigits: precision, maximumFractionDigits: precision } : undefined)} р.
      </span>
    );
  };

  const getSignedDifferenceLabel = React.useCallback((value, precision = 0) => {
    const roundedValue = roundToPrecision(value, precision);
    return `${roundedValue > 0 ? '+' : ''}${roundedValue.toLocaleString(undefined, precision > 0 ? { minimumFractionDigits: precision, maximumFractionDigits: precision } : undefined)} р.`;
  }, []);

  const getItemPrecision = (item) => (
    (item.isBrickAnalog || (item.isCurrentProduct && isBrickContext)) ? 2 : 0
  );

  const getDifferenceColor = React.useCallback((value) => {
    const numericValue = parseFloat(value) || 0;
    return numericValue <= 0 ? '#28a745' : '#ff3333';
  }, []);

  // Для кирпича источник — уже отфильтрованный и отсортированный по цене С доставкой
  // список (sortedBrickAnalogItems), а не сырой itemsWithUpdatedPrices.
  const baseDisplayItems = isBrickContext ? sortedBrickAnalogItems : itemsWithUpdatedPrices;

  const filteredItems = (() => {
    if (!searchQuery.trim()) {
      // Если нет поискового запроса, добавляем текущий товар в начало
      const result = currentProductAsAnalog ? [currentProductAsAnalog, ...baseDisplayItems] : baseDisplayItems;
      console.log('🔍 filteredItems (no search):', result.map(item => ({ label: item.label, isCurrentProduct: item.isCurrentProduct })));
      return result;
    }

    const query = searchQuery.toLowerCase();
    const filtered = baseDisplayItems.filter(
      (item) =>
        (item.label?.toLowerCase() || '').includes(query) ||
        (item.details.manufacturer?.toLowerCase() || '').includes(query) ||
        (item.details.category?.toLowerCase() || '').includes(query)
    );

    // Проверяем, подходит ли текущий товар под поисковый запрос
    const currentProductMatches = currentProductAsAnalog && (
      (currentProductAsAnalog.label?.toLowerCase() || '').includes(query) ||
      (currentProductAsAnalog.details.manufacturer?.toLowerCase() || '').includes(query) ||
      (currentProductAsAnalog.details.category?.toLowerCase() || '').includes(query)
    );

    return currentProductMatches ? [currentProductAsAnalog, ...filtered] : filtered;
  })();

  // Отслеживаем изменение paymentMethod для анимации
  useEffect(() => {
    if (prevPaymentMethod !== paymentMethod) {
      console.log('🔍 Payment method changed from', prevPaymentMethod, 'to', paymentMethod);
      setPrevPaymentMethod(paymentMethod);

      // Запускаем анимацию цен при смене paymentMethod
      const newAnimatingPrices = new Set();
      itemsWithUpdatedPrices.forEach((item, index) => {
        if (!item.isCurrentProduct) {
          newAnimatingPrices.add(index);
        }
      });
      setAnimatingPrices(newAnimatingPrices);

      // Останавливаем анимацию через 500мс
      setTimeout(() => {
        setAnimatingPrices(new Set());
      }, 500);
    }
  }, [paymentMethod, prevPaymentMethod, itemsWithUpdatedPrices]);

  // filteredItems больше не мемоизирован (для кирпича состав/порядок зависит от ещё
  // не загруженной доставки), поэтому ключ эффекта — по составу и порядку значений,
  // а не по ссылке на массив, иначе эффект перезапускался бы на каждый рендер.
  const filteredItemsKey = filteredItems.map((item) => item.value).join('|');

  useEffect(() => {
    // Обновляем initial quantities при изменении отфильтрованных элементов
    const initialQuantities = filteredItems.reduce((acc, item, index) => ({
      ...acc,
      [index]: item.requiredSheets || item.requiredPackages || '1',
    }), {});
    setQuantities(initialQuantities);
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredItemsKey]);

  return (
    <tr className="rd-kp-alts-row">
      <td colSpan={colSpan} className="rd-kp-alts-cell">
        <div className="rd-kp-alts rd-kp-alts--prod">
          {brickDeliveryPending ? (
            <div className="rd-kp-alts__empty">Считаем стоимость доставки для сравнения аналогов…</div>
          ) : (
          <>
          {/* Поиск */}
          <div className="rd-kp-alts__search">
            <input
              type="text"
              placeholder="Поиск по названию, производителю или категории"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {filteredItems.length > 0 ? (
            filteredItems.map((item, index) => {
              const isCurrentProduct = item.isCurrentProduct;
              const isCurrentOnRequest = isCurrentProduct && item.isPriceOnRequest;
              const quantityForItem = parseInt(quantities[index] || item.requiredSheets || item.requiredPackages || 1, 10) || 1;
              const pavingDifferenceTotal = item.isPavingAnalog
                ? (parseFloat(item.priceDifferencePerPackage) || 0) * quantityForItem
                : 0;
              // Для кирпича разница считается по цене С доставкой (getItemPrice), а не по
              // item.priceDifferencePerPackage — та посчитана в useAnalogs.js без доставки.
              const priceDifferencePerPackageForDisplay = item.isBrickAnalog
                ? (parseFloat(getItemPrice(item)) || 0) - currentBrickEffectivePrice
                : item.priceDifferencePerPackage;

              let rowClass = 'rd-kp-alts__row';
              if (isCurrentProduct) {
                rowClass += isCurrentOnRequest ? ' rd-kp-alts__row--request' : ' rd-kp-alts__row--current';
              }

              const subtitle = [
                `Производитель: ${item.details.manufacturer || 'Не указано'}${isCurrentProduct ? ' (текущий)' : ''}`,
                `Категория: ${item.details.category || 'Не указано'}`
              ].filter(Boolean).join(' · ');

              return (
                <div className={rowClass} key={index}>
                  {/* Название + детали */}
                  <div className="rd-kp-alts__title">
                    {item.label || 'Не указано'}
                    <small>{subtitle}</small>
                    {item.details.typeMaterial && (
                      <small className="rd-kp-alts__density">Тип материала: {item.details.typeMaterial}</small>
                    )}
                    {item.details.density && (
                      <small className="rd-kp-alts__density">Плотность: {item.details.density} кг/м³</small>
                    )}
                    {(item.details.brickFormat || item.details.brickVoidness || item.details.brickStrength) && (
                      <small className="rd-kp-alts__density">
                        {[
                          item.details.brickFormat && `НФ: ${item.details.brickFormat}`,
                          item.details.brickVoidness && `Пустотность: ${item.details.brickVoidness}`,
                          item.details.brickStrength && `Прочность: ${item.details.brickStrength}`,
                        ].filter(Boolean).join(' · ')}
                        {item.isHigherStrength && ' (прочнее)'}
                      </small>
                    )}
                    {item.details.compression && (
                      <small className="rd-kp-alts__density">`Прочность: {item.details.compression} кПа`</small>
                    )}
                    {item.isIzolife && item.izolifeComment && (
                      <small className="rd-kp-alts__izolife">{item.izolifeComment}</small>
                    )}
                    {item.isIzolife && item.izolifePhrase && (
                      <small className="rd-kp-alts__izolife">{item.izolifePhrase}</small>
                    )}
                    {(item.isBrickAnalog || (isCurrentProduct && isBrickContext)) && (() => {
                      const deliveryState = getBrickDeliveryState(item);
                      if (deliveryState?.status === 'ok') {
                        return <small className="rd-kp-alts__izolife">с учётом доставки</small>;
                      }
                      if (deliveryState?.status === 'loading') {
                        return <small className="rd-kp-alts__izolife">считаем доставку…</small>;
                      }
                      return null;
                    })()}
                  </div>

                  {/* Количество */}
                  {isCurrentProduct ? (
                    <input
                      className="rd-kp-alts__qty"
                      value={quantities[index] !== undefined ? quantities[index] : (item.requiredSheets || item.requiredPackages || '1')}
                      readOnly
                    />
                  ) : (
                    <NumberInput
                      value={quantities[index] !== undefined ? quantities[index] : (item.requiredSheets || item.requiredPackages || '1')}
                      onChange={(value) => handleQuantityChange(index, value)}
                    />
                  )}

                  {/* Цена за упаковку */}
                  <span className="rd-kp-alts__price">
                    {item.isPriceOnRequest ? (
                      <span className="rd-kp-alts__onreq">По запросу</span>
                    ) : (
                      <AnimatedPrice price={getItemPrice(item)} index={index} isAnimating={animatingPrices.has(index)} precision={getItemPrecision(item)} />
                    )}
                  </span>

                  {/* Итог (с тултипом деталей) */}
                  {isCurrentProduct ? (
                    item.isPriceOnRequest ? (
                      <span className="rd-kp-alts__price rd-kp-alts__onreq">—</span>
                    ) : (
                      <Tooltip
                        title={
                          <div style={{ fontSize: '14px', lineHeight: '1.4' }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#28a745' }}>Детали стоимости:</div>
                            <div style={{ marginBottom: '4px' }}><strong>За упаковку:</strong> {roundToPrecision(getItemPrice(item), getItemPrecision(item)).toLocaleString(undefined, isBrickContext ? { minimumFractionDigits: 2, maximumFractionDigits: 2 } : undefined)} р.</div>
                            <div style={{ marginBottom: '4px' }}><strong>За м²:</strong> {item.priceDifferencePerSquareMeter} р.</div>
                            <div style={{ marginBottom: '8px' }}><strong>За лист:</strong> {item.priceDifferencePerSheet} р.</div>
                          </div>
                        }
                        placement="top"
                        arrow
                      >
                        <span className="rd-kp-alts__price rd-kp-alts__total">
                          {item.totalCost ? (
                            <AnimatedPrice price={item.totalCost} index={index} isAnimating={animatingPrices.has(index)} precision={getItemPrecision(item)} />
                          ) : (
                            <AnimatedPrice price={(getItemPrice(item) || 0) * parseInt(item.requiredSheets || 1)} index={index} isAnimating={animatingPrices.has(index)} precision={getItemPrecision(item)} />
                          )}
                        </span>
                      </Tooltip>
                    )
                  ) : (item.isPavingAnalog || item.isBrickAnalog) ? (
                    <span className="rd-kp-alts__price rd-kp-alts__total">
                      <AnimatedPrice
                        price={(getItemPrice(item) || 0) * parseInt(quantities[index] || item.requiredSheets || 100)}
                        index={index}
                        isAnimating={animatingPrices.has(index)}
                        precision={getItemPrecision(item)}
                      />
                      {!currentProductAsAnalog?.isPriceOnRequest && item.details.price && parseFloat(item.details.price) > 0 && (
                        <small style={{ color: getDifferenceColor((parseFloat(priceDifferencePerPackageForDisplay) || 0) * (parseInt(quantities[index] || item.requiredSheets || 100) || 1)) }}>
                          {getSignedDifferenceLabel((parseFloat(priceDifferencePerPackageForDisplay) || 0) * (parseInt(quantities[index] || item.requiredSheets || 100) || 1), getItemPrecision(item))}
                        </small>
                      )}
                    </span>
                  ) : (
                    <Tooltip
                      title={
                        <div style={{ fontSize: '14px', lineHeight: '1.4' }}>
                          <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#28a745' }}>Детализация выгоды:</div>
                          {item.details.category !== "OSB" && (
                            <div style={{ marginBottom: '4px' }}>
                              <strong>За упаковку:</strong> {parseInt(getItemPrice(item) || 0)} р.
                              {!currentProductAsAnalog?.isPriceOnRequest && (
                                <span style={{ color: item.priceDifferencePerPackage <= 0 ? '#28a745' : '#ff3333' }}>
                                  ({item.priceDifferencePerPackage <= 0 ? "" : "+"}{parseInt(item.priceDifferencePerPackage || 0)} р.)
                                </span>
                              )}
                            </div>
                          )}
                          <div style={{ marginBottom: '4px' }}>
                            <strong>За м²:</strong> {Math.round(item.analogPricePerSquareMeter || (item.details.area ? getItemPrice(item) / parseFloat(item.details.area) : 0))} р.
                            {!currentProductAsAnalog?.isPriceOnRequest && (
                              <span style={{ color: item.priceDifferencePerSquareMeter <= 0 ? '#28a745' : '#ff3333' }}>
                                ({item.priceDifferencePerSquareMeter <= 0 ? "" : "+"}{parseInt(item.priceDifferencePerSquareMeter || 0)} р.)
                              </span>
                            )}
                          </div>
                          <div style={{ marginBottom: '8px' }}>
                            <strong>За лист:</strong> {Math.round(item.analogPricePerSheet ||
                              (item.details.category && item.details.category.includes("OSB")
                                ? getItemPrice(item)
                                : item.details.sheetsInsulation ? getItemPrice(item) / parseInt(item.details.sheetsInsulation)
                                  : 0))} р.
                            {!currentProductAsAnalog?.isPriceOnRequest && (
                              <span style={{ color: item.priceDifferencePerSheet <= 0 ? '#28a745' : '#ff3333' }}>
                                ({item.priceDifferencePerSheet <= 0 ? "" : "+"}{parseInt(item.priceDifferencePerSheet || 0)} р.)
                              </span>
                            )}
                          </div>
                        </div>
                      }
                      placement="top"
                      arrow
                    >
                      <span className="rd-kp-alts__price rd-kp-alts__total">
                        <AnimatedPrice
                          price={(getItemPrice(item) || 0) * parseInt(quantities[index] || item.requiredSheets || 100)}
                          index={index}
                          isAnimating={animatingPrices.has(index)}
                        />
                        {!currentProductAsAnalog?.isPriceOnRequest && item.details.price && parseFloat(item.details.price) > 0 && Number(item.volumeBenefit) < 0 && (
                          <small>{parseInt(item.volumeBenefit || 0)?.toLocaleString()} р.</small>
                        )}
                      </span>
                    </Tooltip>
                  )}

                  {/* Действие */}
                  {isCurrentProduct ? (
                    <button className="rd-kp-alts__choose" type="button">текущий</button>
                  ) : (
                    <button
                      className="rd-kp-alts__choose"
                      type="button"
                      onClick={() => handleSelect(item, index)}
                      disabled={!!errors[index]}
                    >
                      выбрать
                    </button>
                  )}
                </div>
              );
            })
          ) : (
            <div className="rd-kp-alts__empty">Нет аналогов, соответствующих запросу</div>
          )}
          </>
          )}
        </div>
      </td>
    </tr>
  );
};

export default AnalogsList;
